import { Timestamp } from 'firebase/firestore';

import Season from '../types/Season';

export interface CategorizedSeasons {
  current: Season | null;
  last: Season | null;
  older: Season[];
}

/**
 * Derive the current active season from a list of seasons.
 * Returns the season where today falls within start/end range, or undefined if none match.
 */
export function getCurrentSeason(seasons: Season[] | null | undefined): Season | undefined {
  if (!seasons || seasons.length === 0) return undefined;

  const now = new Date();
  return seasons.find((season) => {
    const startDate = (season.startTime as Timestamp).toDate();
    const endDate = (season.endTime as Timestamp).toDate();
    return startDate <= now && endDate >= now;
  });
}

/**
 * Derive the current season name from a list of seasons.
 */
export function getCurrentSeasonName(seasons: Season[] | null | undefined): string | undefined {
  return getCurrentSeason(seasons)?.name;
}

/**
 * Categorizes seasons into current (active), last (most recent past), and older (remaining past).
 * Seasons are sorted by date, with most recent first.
 */
export function categorizeSeasons(seasons: Season[] | null | undefined): CategorizedSeasons {
  if (!seasons || seasons.length === 0) {
    return { current: null, last: null, older: [] };
  }

  const now = new Date();
  const active: Season[] = [];
  const past: Season[] = [];

  seasons.forEach((season) => {
    // Skip catch-all seasons
    if (season.isCatchAll) {
      return;
    }

    const startDate = (season.startTime as Timestamp).toDate();
    const endDate = (season.endTime as Timestamp).toDate();

    if (endDate < now) {
      past.push(season);
    } else if (startDate <= now) {
      active.push(season);
    }
    // Future seasons are ignored for results display
  });

  // Sort active by start time (most recent first)
  active.sort(
    (a, b) => (b.startTime as Timestamp).toMillis() - (a.startTime as Timestamp).toMillis()
  );

  // Sort past by end time (most recent first)
  past.sort((a, b) => (b.endTime as Timestamp).toMillis() - (a.endTime as Timestamp).toMillis());

  return {
    current: active[0] || null,
    last: past[0] || null,
    older: past.slice(1),
  };
}
